'use client';

import Link from 'next/link';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';
import { pageHref, paginationItems } from '../../../lib/pagination';

const STEP_CLASS =
  'inline-flex items-center gap-1 px-3 py-2 text-[13px] font-medium text-gray-600 rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-colors';
const NUMBER_CLASS =
  'inline-flex min-w-9 justify-center px-2.5 py-2 text-[13px] font-medium tabular-nums text-gray-600 rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-colors';

// Enlaces `<a href>` numerados (los renderiza el servidor): así Google llega a cualquier página del
// listado en pocos saltos, no solo a la siguiente.
export default function Pagination({ basePath, page, totalPages }) {
  if (totalPages <= 1) return null;

  const track = (direction, to) =>
    trackEvent(EVENTS.CATEGORY_PAGINATION_USED, {
      direction,
      from_page: page,
      to_page: to,
      total_pages: totalPages
    });

  return (
    <nav aria-label="Paginación" className="mt-10 flex flex-col items-center gap-3">
      <div className="flex items-center justify-center gap-2">
        {page > 1 && (
          <Link
            href={pageHref(basePath, page - 1)}
            rel="prev"
            onClick={() => track('previous', page - 1)}
            className={STEP_CLASS}
          >
            <ChevronLeftIcon className="w-3 h-3" />
            Anterior
          </Link>
        )}
        <span className="px-3 py-2 text-[13px] text-gray-500">
          Página {page} de {totalPages.toLocaleString('es-AR')}
        </span>
        {page < totalPages && (
          <Link
            href={pageHref(basePath, page + 1)}
            rel="next"
            onClick={() => track('next', page + 1)}
            className={STEP_CLASS}
          >
            Siguiente
            <ChevronRightIcon className="w-3 h-3" />
          </Link>
        )}
      </div>
      <ol className="flex flex-wrap items-center justify-center gap-1.5">
        {paginationItems(page, totalPages).map((n, i) =>
          n === null ? (
            <li key={`gap-${i}`} aria-hidden="true" className="px-1 text-[13px] text-gray-400">
              …
            </li>
          ) : (
            <li key={n}>
              {n === page ? (
                <span
                  aria-current="page"
                  className={`${NUMBER_CLASS} border-primary-200 bg-primary-50 text-primary-700`}
                >
                  {n.toLocaleString('es-AR')}
                </span>
              ) : (
                <Link href={pageHref(basePath, n)} onClick={() => track('jump', n)} className={NUMBER_CLASS}>
                  {n.toLocaleString('es-AR')}
                </Link>
              )}
            </li>
          )
        )}
      </ol>
    </nav>
  );
}
